import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Locale } from '@diana/shared'
import { useContent } from '../../hooks/useContent.js'
import { OfferCard } from '../../components/OfferCard.js'
import { SectionHead } from '../../components/Section.js'
import { useLocale, usePath } from '../../i18n/index.js'

type Step = { n: string; title: string; body: string }

type PageStrings = {
  loading: string
  eyebrow: string
  heading: ReactNode
  lead: string
  leadSecondary: string
  heroButton: string
  howEyebrow: string
  howTitle: string
  steps: Step[]
  pricingEyebrow: string
  pricingTitle: ReactNode
  pricingIntro: string
  noOffers: string
  includedEyebrow: string
  includedTitle: string
  included: string[]
  ctaHeading: ReactNode
  ctaBody: string
  ctaButton: string
}

const T: Record<Locale, PageStrings> = {
  pl: {
    loading: 'Wczytuję…',
    eyebrow: 'Ucz się ze mną',
    heading: (
      <>
        Lekcje dopasowane <em className="not-italic text-gold">do Ciebie</em>
      </>
    ),
    lead: 'Indywidualne podejście i nauka dostosowana do Twoich potrzeb, poziomu oraz celów.',
    leadSecondary:
      'Niezależnie od tego, czy chcesz swobodniej mówić, poprawić gramatykę, przygotować się do egzaminu czy po prostu poczuć się pewniej w języku polskim — dopasuję lekcje do Ciebie.',
    heroButton: 'Umów lekcję próbną',
    howEyebrow: 'Krok po kroku',
    howTitle: 'Jak to działa?',
    steps: [
      {
        n: '01',
        title: 'Przyjdź na lekcję próbną',
        body: 'Poznamy się, porozmawiamy o Twoim poziomie, potrzebach i celach.',
      },
      {
        n: '02',
        title: 'Stworzę dla Ciebie indywidualny program',
        body: 'Na podstawie rozmowy przygotuję plan nauki dopasowany do Ciebie.',
      },
      {
        n: '03',
        title: 'Zaczynamy naukę',
        body: 'Pracujemy krok po kroku nad tym, czego naprawdę potrzebujesz.',
      },
    ],
    pricingEyebrow: 'Cennik',
    pricingTitle: (
      <>
        Przejrzyste ceny, <em className="not-italic text-gold">bez ukrytych kosztów</em>
      </>
    ),
    pricingIntro: 'To, co widzisz, to całość. Nie ma opłaty wpisowej, nie ma pakietów na rok z góry.',
    noOffers: 'Brak ofert. Dodaj je w panelu → Cennik.',
    includedEyebrow: 'W cenie',
    includedTitle: 'Co dostajesz',
    included: [
      'Materiały przygotowane pod Ciebie, nie kserówki z podręcznika',
      'Zadania domowe z rzeczywistą korektą, nie tylko „ok"',
      'Kontakt między lekcjami, jeśli coś Cię zablokuje',
      'Notatki z każdej lekcji, do których zawsze wrócisz',
      'Uczciwa informacja zwrotna — także wtedy, gdy nie jest miła',
    ],
    ctaHeading: (
      <>
        Nie wiesz, co wybrać? <em className="not-italic text-gold-300">Zacznij od lekcji próbnej</em>
      </>
    ),
    ctaBody:
      'Bez zobowiązań. Doradzę uczciwie — także wtedy, gdy lepszym wyborem będzie ktoś inny.',
    ctaButton: 'Umów lekcję próbną',
  },
  uk: {
    loading: 'Завантажую…',
    eyebrow: 'Навчання зі мною',
    heading: (
      <>
        Уроки, підлаштовані <em className="not-italic text-gold">під тебе</em>
      </>
    ),
    lead: 'Індивідуальний підхід і навчання, підлаштоване під твої потреби, рівень і цілі.',
    leadSecondary:
      'Незалежно від того, чи хочеш ти вільніше говорити, підтягнути граматику, підготуватися до іспиту чи просто почуватися впевненіше в польській — я підлаштую уроки під тебе.',
    heroButton: 'Записатися на пробний урок',
    howEyebrow: 'Крок за кроком',
    howTitle: 'Як це працює?',
    steps: [
      {
        n: '01',
        title: 'Приходь на пробний урок',
        body: 'Познайомимося, поговоримо про твій рівень, потреби й цілі.',
      },
      {
        n: '02',
        title: 'Я створю для тебе індивідуальну програму',
        body: 'На основі розмови підготую план навчання, підлаштований під тебе.',
      },
      {
        n: '03',
        title: 'Починаємо навчання',
        body: 'Працюємо крок за кроком над тим, що тобі справді потрібно.',
      },
    ],
    pricingEyebrow: 'Ціни',
    pricingTitle: (
      <>
        Прозорі ціни, <em className="not-italic text-gold">без прихованих витрат</em>
      </>
    ),
    pricingIntro: 'Те, що ти бачиш, — це все. Немає вступного внеску, немає пакетів на рік наперед.',
    noOffers: 'Немає пропозицій. Додай їх у панелі → Ціни.',
    includedEyebrow: 'У ціні',
    includedTitle: 'Що ти отримуєш',
    included: [
      'Матеріали, підготовлені під тебе, а не ксерокопії з підручника',
      'Домашні завдання зі справжньою корекцією, а не просто «ок»',
      'Зв’язок між уроками, якщо щось тебе застопорить',
      'Нотатки з кожного уроку, до яких завжди можна повернутися',
      'Чесний зворотний зв’язок — навіть тоді, коли він не надто приємний',
    ],
    ctaHeading: (
      <>
        Не знаєш, що обрати? <em className="not-italic text-gold-300">Почни з пробного уроку</em>
      </>
    ),
    ctaBody:
      'Без зобов’язань. Пораджу чесно — навіть якщо кращим вибором буде хтось інший.',
    ctaButton: 'Записатися на пробний урок',
  },
  ru: {
    loading: 'Загружаю…',
    eyebrow: 'Занятия со мной',
    heading: (
      <>
        Занятия, подстроенные <em className="not-italic text-gold">под тебя</em>
      </>
    ),
    lead: 'Индивидуальный подход и обучение, подстроенное под твои потребности, уровень и цели.',
    leadSecondary:
      'Независимо от того, хочешь ли ты свободнее говорить, подтянуть грамматику, подготовиться к экзамену или просто чувствовать себя увереннее в польском — я подстрою занятия под тебя.',
    heroButton: 'Записаться на пробный урок',
    howEyebrow: 'Шаг за шагом',
    howTitle: 'Как это работает?',
    steps: [
      {
        n: '01',
        title: 'Приходи на пробный урок',
        body: 'Познакомимся, поговорим о твоём уровне, потребностях и целях.',
      },
      {
        n: '02',
        title: 'Я составлю для тебя индивидуальную программу',
        body: 'На основе разговора подготовлю план обучения, подстроенный под тебя.',
      },
      {
        n: '03',
        title: 'Начинаем учиться',
        body: 'Работаем шаг за шагом над тем, что тебе действительно нужно.',
      },
    ],
    pricingEyebrow: 'Цены',
    pricingTitle: (
      <>
        Прозрачные цены, <em className="not-italic text-gold">без скрытых расходов</em>
      </>
    ),
    pricingIntro: 'То, что ты видишь, — это всё. Нет вступительного взноса, нет пакетов на год вперёд.',
    noOffers: 'Пока нет предложений. Добавь их в панели → Цены.',
    includedEyebrow: 'В цене',
    includedTitle: 'Что ты получаешь',
    included: [
      'Материалы, подготовленные под тебя, а не ксерокопии из учебника',
      'Домашние задания с настоящей проверкой, а не просто «ок»',
      'Связь между уроками, если что-то тебя застопорит',
      'Заметки с каждого урока, к которым всегда можно вернуться',
      'Честная обратная связь — даже когда она не слишком приятная',
    ],
    ctaHeading: (
      <>
        Не знаешь, что выбрать? <em className="not-italic text-gold-300">Начни с пробного урока</em>
      </>
    ),
    ctaBody:
      'Без обязательств. Посоветую честно — даже если лучшим выбором окажется кто-то другой.',
    ctaButton: 'Записаться на пробный урок',
  },
}

export function LearnPage() {
  const locale = useLocale()
  const to = usePath()
  const { data, isLoading } = useContent(locale)
  const s = T[locale]

  if (isLoading || !data) {
    return <div className="mx-auto max-w-6xl px-5 py-32 font-light">{s.loading}</div>
  }

  return (
    <>
      <section className="mx-auto max-w-6xl px-5 pb-4 pt-16 lg:px-8 lg:pt-24">
        <p className="eyebrow">{s.eyebrow}</p>
        <h1 className="h-display mt-5 max-w-3xl text-[2.6rem] leading-[1.1] sm:text-5xl">
          {s.heading}
        </h1>
        <div className="rule-gold mt-8 w-28" />
        <p className="lead mt-8 max-w-2xl">{s.lead}</p>
        <p className="mt-5 max-w-2xl leading-relaxed font-light">{s.leadSecondary}</p>
        <Link to={to('/rezerwacja')} className="btn btn-gold mt-9">
          {s.heroButton}
        </Link>
      </section>

      {/* ── how it works ──────────────────────────────────────────────── */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <SectionHead eyebrow={s.howEyebrow} title={s.howTitle} />

          <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-sky-200 bg-sky-200 md:grid-cols-3">
            {s.steps.map((step) => (
              <article key={step.n} className="bg-porcelain p-8 lg:p-10">
                <p className="h-display text-4xl text-gold-300">{step.n}</p>
                <h3 className="h-condensed mt-4 text-lg">{step.title}</h3>
                <div className="rule-gold mt-4 w-10" />
                <p className="mt-5 text-[0.95rem] leading-relaxed">{step.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── pricing ───────────────────────────────────────────────────── */}
      <section className="bg-ivory-deep py-24 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <SectionHead eyebrow={s.pricingEyebrow} title={s.pricingTitle} intro={s.pricingIntro} />

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.offers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} />
            ))}
          </div>

          {data.offers.length === 0 && (
            <p className="mt-12 border border-dashed border-sky-300 p-14 text-center font-light">
              {s.noOffers}
            </p>
          )}
        </div>
      </section>

      {/* ── what's included ───────────────────────────────────────────── */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr]">
            <SectionHead eyebrow={s.includedEyebrow} title={s.includedTitle} />

            <ul className="space-y-0 divide-y divide-sky-200 border-y border-sky-200">
              {s.included.map((item) => (
                <li key={item} className="flex gap-5 py-5">
                  <span aria-hidden className="mt-2.5 h-px w-6 shrink-0 bg-gold" />
                  <span className="text-[1.05rem] leading-relaxed font-light">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 text-center lg:py-24">
        <div className="mx-auto max-w-2xl px-5">
          <h2 className="h-display text-3xl leading-tight text-ivory sm:text-4xl">{s.ctaHeading}</h2>
          <p className="mx-auto mt-6 max-w-lg leading-relaxed font-light text-sky-200/80">
            {s.ctaBody}
          </p>
          <Link
            to={to('/rezerwacja')}
            className="btn mt-9 border border-gold-300 bg-gold-300 text-ink hover:bg-transparent hover:text-gold-300"
          >
            {s.ctaButton}
          </Link>
        </div>
      </section>
    </>
  )
}
